import { AHText, Box, useTheme, AHIcon } from "arkh";
import React, { useCallback, useMemo } from "react";
import { View } from "react-native";
import { SuggestionItem } from "./SuggestionItem/SuggestionItem";
import { EmaginItem } from "./SuggestionItem/EmaginItem";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useDeviceOrientation } from "../../common/Utils/useDeviceOrientation";
import { FormattedMessage } from "react-intl";
import Color from "color";
import { SuggestionItem_File$key } from "./SuggestionItem/__generated__/SuggestionItem_File.graphql";
import { EmaginItem_IntegrationEmaginFlyer$key } from "./SuggestionItem/__generated__/EmaginItem_IntegrationEmaginFlyer.graphql";
import { openFileType } from "./useOpenFile";

type ListItemType =
  | {
      type: "file";
      key: SuggestionItem_File$key & { id: string };
    }
  | {
      type: "integration";
      key: EmaginItem_IntegrationEmaginFlyer$key & { id: string };
    }
  | "RECENT_HEADER"
  | "NEWLY_UPLOADED_HEADER";

export function Recents(props: {
  integrationEmaginFlyers: (EmaginItem_IntegrationEmaginFlyer$key & {
    id: string;
  })[];
  recentUploaded: readonly (SuggestionItem_File$key & { id: string })[];
  suggestedFiles: readonly (SuggestionItem_File$key & { id: string })[];
  modal?: boolean;
  onPressFile: openFileType;
  onPressIntegrationFile?(_: { id: string; title: string }): void;
}) {
  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const orientation = useDeviceOrientation();
  const numColumns = useMemo(() => {
    if (isCompact) {
      return 2;
    }
    if (orientation.landscape && !props.modal) {
      return 6;
    }
    return 4;
  }, [isCompact, orientation.landscape, props.modal]);

  const itemWidth = useMemo(
    () => `${100 / numColumns}%` as const,
    [numColumns]
  );
  const renderItem = useCallback(
    ({ item }: { item: ListItemType }) => {
      if (item === "RECENT_HEADER") {
        return <SuggestionsHeader />;
      }
      if (item === "NEWLY_UPLOADED_HEADER") {
        return <SuggestionsHeader />;
      }
      if (item.type === "integration") {
        return (
          <Box style={{ width: itemWidth }} key={item.key.id}>
            <EmaginItem
              integrationEmaginFlyer={item.key}
              onPress={props.onPressIntegrationFile}
            />
          </Box>
        );
      }
      return (
        <Box style={{ width: itemWidth, overflow: "hidden" }} key={item.key.id}>
          <SuggestionItem file={item.key} openFile={props.onPressFile} />
        </Box>
      );
    },
    [itemWidth, props.onPressFile, props.onPressIntegrationFile]
  );

  const suggestions: ListItemType[] = useMemo(
    () =>
      [
        ...props.integrationEmaginFlyers.map((key) => ({
          type: "integration" as const,
          key,
        })),
        ...props.suggestedFiles.map((key) => ({
          type: "file" as const,
          key,
        })),
      ].filter((i) => i?.key?.id),

    [props.integrationEmaginFlyers, props.suggestedFiles]
  );

  const recentlyUploaded: ListItemType[] = useMemo(
    () =>
      props.recentUploaded
        .map((key) => ({
          type: "file" as const,
          key,
        }))
        .filter((i) => i?.key?.id),
    [props.recentUploaded]
  );
  return (
    <View>
      {recentlyUploaded.length > 0 ? <RecentUploadHeader /> : null}
      <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
        {recentlyUploaded.map((ld) => renderItem({ item: ld }))}
      </View>
      <SuggestionsHeader />
      <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
        {suggestions.map((ld) => renderItem({ item: ld }))}
      </View>
    </View>
  );
}

function RecentUploadHeader() {
  const theme = useTheme();
  return (
    <Box
      style={{ flexDirection: "row", alignItems: "center" }}
      spaceTop="medium"
      spaceBottom="xlarge"
    >
      <AHIcon type="calendar" customColor={theme.primary} forceSize={22} />
      <AHText
        spaceLeft="small"
        type="title-small"
        style={{
          backgroundColor: Color(theme.background).alpha(0.5).toString(),
        }}
      >
        <FormattedMessage
          defaultMessage="Recently Uploaded"
          description="FilesScreen Uploaded Recently"
        />
      </AHText>
    </Box>
  );
}

function SuggestionsHeader() {
  const theme = useTheme();
  return (
    <Box
      style={{ flexDirection: "row", alignItems: "center" }}
      spaceTop="medium"
      spaceBottom="xlarge"
    >
      <AHIcon type="zap" customColor={theme.primary} forceSize={22} />
      <AHText
        spaceLeft="small"
        type="title-small"
        style={{
          backgroundColor: Color(theme.background).alpha(0.5).toString(),
        }}
      >
        <FormattedMessage
          defaultMessage="Suggestions"
          description="FilesScreen Suggestions title"
        />
      </AHText>
    </Box>
  );
}
